import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from './constants';

export function useStripeActions({ API, getAuthHeader, fetchData, calculateMontantTTC, selectedDocument: selectedDocument, setSelectedDocument, setShowStripeSepaModal, setStripeSepaType, setStripeProcessing, setShowStripeCBModal, sepaModalEmail, setSepaModalEmail, sepaModalName, setSepaModalName }) {

  // Ouvrir le modal Stripe SEPA
  const openStripeSepaModal = (doc, type = 'core') => {
    setSelectedDocument(doc);
    setStripeSepaType(type);
    setSepaModalEmail(doc.email || doc.membre_email || doc.member?.email || '');
    setSepaModalName(doc.raison_sociale || doc.nom || doc.member?.nom || '');
    setShowStripeSepaModal(true);
  };

  // Configurer le prélèvement SEPA
  const setupStripeSEPA = async (doc, type) => {
    if (!doc.email) {
      toast.error('Email client requis pour configurer le prélèvement SEPA. Veuillez d\'abord ajouter un email à cette souscription.');
      return;
    }
    
    setStripeProcessing(true);
    try {
      const response = await axios.post(
        `${API}/souscriptions/admin/${doc.id}/setup-sepa`,
        {
          sepa_type: type,
          customer_email: doc.email,
          customer_name: doc.raison_sociale,
          amount: calculateMontantTTC(doc) * 100,
          description: `Contribution ESS - ${doc.numero_police}`,
          mandate_data: {
            notification_days: type === 'core' ? 14 : 1,
            can_refund: type === 'core'
          }
        },
        { headers: getAuthHeader() }
      );
      
      if (response.data.checkout_url) {
        window.open(response.data.checkout_url, '_blank');
        toast.success(`Lien de configuration SEPA ${type.toUpperCase()} envoyé`);
      } else {
        toast.success(`Mandat SEPA ${type.toUpperCase()} configuré avec succès`);
      }
      
      setShowStripeSepaModal(false);
      fetchData(true);
    } catch (error) {
      let errorMessage = 'Erreur lors de la configuration SEPA';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg || e.message).join(', ');
        }
      } else if (Array.isArray(error.response?.data)) {
        errorMessage = error.response.data.map(e => e.msg || e.message).join(', ');
      }
      if (errorMessage.includes('customer_email') || errorMessage.includes('Email')) {
        errorMessage = 'Email client requis pour configurer le prélèvement SEPA';
      }
      toast.error(errorMessage);
    } finally {
      setStripeProcessing(false);
    }
  };

  // Configurer SEPA avec email saisi manuellement
  const setupStripeSEPAWithEmail = async (doc, type) => {
    if (!sepaModalEmail) {
      toast.error('Email client requis pour configurer le prélèvement SEPA');
      return;
    }
    if (!sepaModalName) {
      toast.error('Nom du titulaire requis');
      return;
    }

    setStripeProcessing(true);
    try {
      if (sepaModalEmail !== (doc.email || '')) {
        try {
          await axios.put(
            `${API}/souscriptions/admin/${doc.id}/update-client-email`,
            { email: sepaModalEmail },
            { headers: getAuthHeader() }
          );
        } catch {
        }
      }

      const response = await axios.post(
        `${API}/souscriptions/admin/${doc.id}/setup-sepa`,
        {
          sepa_type: type,
          customer_email: sepaModalEmail,
          customer_name: sepaModalName,
          amount: calculateMontantTTC(doc) * 100,
          description: `Contribution ESS - ${doc.numero_police}`,
          mandate_data: {
            notification_days: type === 'core' ? 14 : 1,
            can_refund: type === 'core'
          }
        },
        { headers: getAuthHeader() }
      );
      
      if (response.data.checkout_url) {
        window.open(response.data.checkout_url, '_blank');
        toast.success(`Lien de configuration SEPA ${type.toUpperCase()} envoyé`);
      } else {
        toast.success(`Mandat SEPA ${type.toUpperCase()} configuré avec succès`);
      }
      
      setShowStripeSepaModal(false);
      fetchData(true);
    } catch (error) {
      let errorMessage = 'Erreur lors de la configuration SEPA';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg || e.message).join(', ');
        }
      }
      toast.error(errorMessage);
    } finally {
      setStripeProcessing(false);
    }
  };

  // Programmer un prélèvement SEPA pour une échéance
  const scheduleSepaDebit = async (doc, echeance, echeanceIndex) => {
    setStripeProcessing(true);
    try {
      await axios.post(
        `${API}/souscriptions/admin/${doc.id}/schedule-sepa-debit`,
        {
          echeance_index: echeanceIndex,
          amount: echeance.montant * 100,
          scheduled_date: echeance.date_prevue,
          description: `Échéance ${echeanceIndex + 1} - ${doc.numero_police}`
        },
        { headers: getAuthHeader() }
      );
      
      toast.success(`Prélèvement SEPA programmé pour le ${formatDate(echeance.date_prevue)}`);
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la programmation');
    } finally {
      setStripeProcessing(false);
    }
  };

  // Paiement CB Stripe
  const initiateStripeCBPayment = async (doc, amount = null) => {
    setStripeProcessing(true);
    try {
      const montant = amount || calculateMontantTTC(doc);
      const response = await axios.post(
        `${API}/souscriptions/admin/${doc.id}/create-checkout`,
        {
          amount: montant * 100,
          payment_method: 'card',
          description: `Contribution ESS - ${doc.numero_police}`,
          success_url: window.location.href,
          cancel_url: window.location.href
        },
        { headers: getAuthHeader() }
      );
      
      if (response.data.checkout_url) {
        window.open(response.data.checkout_url, '_blank');
        toast.success('Page de paiement Stripe ouverte');
      }
      
      setShowStripeCBModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'initialisation du paiement');
    } finally {
      setStripeProcessing(false);
    }
  };

  return {
    openStripeSepaModal,
    setupStripeSEPA,
    setupStripeSEPAWithEmail,
    scheduleSepaDebit,
    initiateStripeCBPayment,
  };
}
